<script setup>
/**
 * Finish Chat Room Setup - AI Focus
 * Página de conclusão da criação da sala de chat (Passo 3 do wizard)
 */
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const isLoading = ref(true);
const roomId = computed(() => Number(route.params.roomId));
const room = computed(() =>
  store.getters['chatRooms/getChatRoom'](roomId.value)
);
const members = computed(() => room.value?.members || []);

const steps = computed(() => [
  {
    number: 1,
    title: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_1_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_1_DESC'),
  },
  {
    number: 2,
    title: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_2_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_2_DESC'),
  },
  {
    number: 3,
    title: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_3_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_3_DESC'),
  },
]);

const currentStep = 3;

onMounted(async () => {
  try {
    await store.dispatch('chatRooms/show', roomId.value);
    await store.dispatch('chatRooms/getMembers', roomId.value);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="flex h-full overflow-auto p-6">
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-full h-full"
    >
      <Spinner />
    </div>
    <div
      v-else
      class="flex w-full max-w-5xl mx-auto border border-n-weak rounded-xl bg-n-solid-2"
    >
      <!-- Left side - Steps -->
      <div class="w-64 flex-shrink-0 p-6 border-r border-n-weak">
        <div class="relative">
          <!-- Connecting line -->
          <div class="absolute left-4 top-8 w-0.5 bg-n-weak step-line" />

          <div class="space-y-6 relative">
            <div v-for="step in steps" :key="step.number" class="flex gap-3">
              <div
                class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium flex-shrink-0 z-10"
                :class="
                  step.number === currentStep
                    ? 'bg-woot-500 text-white'
                    : step.number < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-n-solid-3 border border-n-weak text-n-slate-11'
                "
              >
                <fluent-icon
                  v-if="step.number < currentStep"
                  icon="checkmark"
                  size="16"
                />
                <span v-else>{{ step.number }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium"
                  :class="
                    step.number === currentStep
                      ? 'text-woot-500'
                      : step.number < currentStep
                        ? 'text-green-500'
                        : 'text-n-slate-11'
                  "
                >
                  {{ step.title }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ step.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Success message -->
      <div class="flex-1 p-6 overflow-auto">
        <div class="max-w-md">
          <!-- Success Icon -->
          <div
            class="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-green-100 dark:bg-green-900/30"
          >
            <fluent-icon
              icon="checkmark-circle"
              size="32"
              class="text-green-600 dark:text-green-400"
            />
          </div>

          <h2 class="text-xl font-semibold text-n-slate-12 mb-1">
            {{ $t('CHAT_ROOMS_SETTINGS.FINISH.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mb-6">
            {{ $t('CHAT_ROOMS_SETTINGS.FINISH.DESC') }}
          </p>

          <!-- Room Info Card -->
          <div class="bg-n-solid-2 rounded-xl p-5 mb-6">
            <div class="flex items-center gap-4 mb-4">
              <Avatar
                :src="room?.avatar_url"
                :name="room?.name || 'Room'"
                :size="48"
              />
              <div class="flex-1 min-w-0">
                <h3 class="font-semibold text-n-slate-12 truncate">
                  {{ room?.name }}
                </h3>
                <p class="text-sm text-n-slate-11 truncate">
                  {{
                    room?.description ||
                    $t('CHAT_ROOMS_SETTINGS.FINISH.NO_DESCRIPTION')
                  }}
                </p>
              </div>
            </div>

            <div class="border-t border-n-weak pt-4">
              <p class="text-sm font-medium text-n-slate-12 mb-3">
                {{ $t('CHAT_ROOMS_SETTINGS.FINISH.MEMBERS') }} ({{
                  members.length
                }})
              </p>
              <div v-if="members.length > 0" class="flex flex-wrap gap-2">
                <div
                  v-for="member in members.slice(0, 5)"
                  :key="member.id"
                  class="flex items-center gap-2 px-2.5 py-1.5 bg-n-alpha-1 rounded-lg"
                >
                  <Avatar
                    :src="member.avatar_url"
                    :name="member.name"
                    :size="20"
                  />
                  <span class="text-xs text-n-slate-12">{{ member.name }}</span>
                </div>
                <div
                  v-if="members.length > 5"
                  class="flex items-center px-2.5 py-1.5 bg-n-alpha-1 rounded-lg"
                >
                  <span class="text-xs text-n-slate-11">
                    +{{ members.length - 5 }}
                    {{ $t('CHAT_ROOMS_SETTINGS.FINISH.MORE') }}
                  </span>
                </div>
              </div>
              <p v-else class="text-sm text-n-slate-10">
                {{ $t('CHAT_ROOMS_SETTINGS.FINISH.NO_MEMBERS') }}
              </p>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-3">
            <router-link
              :to="{ name: 'chat_rooms', params: { roomId: roomId } }"
              class="flex-1"
            >
              <Button
                :label="$t('CHAT_ROOMS_SETTINGS.FINISH.GO_TO_CHAT')"
                icon="i-lucide-message-square"
                class="w-full"
              />
            </router-link>
            <router-link :to="{ name: 'settings_chat_rooms_list' }">
              <Button
                :label="$t('CHAT_ROOMS_SETTINGS.FINISH.GO_TO_SETTINGS')"
                slate
                faded
              />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.step-line {
  height: calc(100% - 2rem);
}
</style>
